import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType, OnInitEffects} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {routerRequestAction} from '@ngrx/router-store';
import {Action, select, Store} from '@ngrx/store';
import {BenutzerprofilEinstellungenApi, BenutzerProfilEinstellungenApiModel, PartnerStammdatenApiModel} from 'cargo-hub-ui-customer-api';
import {AutorisierungenApiInternal, CargopushApiInternal, CargopushStatusInfoApiModelInternal} from 'cargo-hub-ui-internal-api';
import {combineLatestWith, concatMap, EMPTY, from, fromEvent, interval, merge, Observable, of, throttleTime, timeout, timer} from 'rxjs';
import {catchError, debounceTime, delay, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import Smartlook from 'smartlook-client';
import {CookieGroup} from 'src/app/services/onetrust/cookie-group';
import {OnetrustService} from 'src/app/services/onetrust/onetrust.service';
import {environment} from '../../../environments/environment';
import {ErrorService} from '../../cd-common/service/error.service';
import {UserHelper} from '../../model/user-helper';
import {NotificationHelper} from '../../root-store/notification/notification-helper';
import {notificationCreate, notificationDelete, notificationDeleteAll} from '../../root-store/notification/notification.actions';
import {selectPanelNotifications} from '../../root-store/notification/notification.selectors';
import {AuthService} from '../../services/auth/auth.service';
import {decodeJwt, ParsedJwtToken} from '../../services/auth/decode-jwt';
import {User} from '../../services/auth/userEntity';
import {DateService} from '../../services/date/date.service';
import {ForwardToLastAreaService} from '../../services/forward-to-last-area/forward-to-last-area.service';
import {LocalStorageService} from '../../services/local-storage/local-storage.service';
import {filterSettingsKeys} from '../../services/local-storage/persist-filter-settings.service';
import {PartnerService} from '../../services/partner/partner.service';
import {SimulatedDateService} from '../../services/simulated-date/simulated-date.service';
import {WebsocketService} from '../../services/websocket/websocket.service';
import {serializeError} from '../../util/generalFunctions';
import {MonitoringHelper} from '../../util/monitoring/monitoring-helper';
import {notEmpty} from '../../util/type-helper';
import {Stoerungsmeldung, Tipp} from '../cms-types';
import {CmsService} from '../cms.service';
import {RequestSuccessInterceptor} from '../interceptors/request-success.interceptor';
import {SessionAnalyseComponent} from '../session-analyse/session-analyse.component';
import {TermsAndConditionsComponent} from '../terms-and-conditions/terms-and-conditions.component';
import {
  authInitAction,
  changeUserProfileSettings,
  changeUserProfileSettingsFailure,
  changeUserProfileSettingsSuccess,
  loadCargoPushStatusInfoSuccess,
  loadUserProfileSettingsFailure,
  loadUserProfileSettingsSuccess,
  loggedInAction,
  logoutAction,
  newContextAction,
  queryRoleAssignations,
  queryRoleAssignationsSuccess,
  setDesiredRedirectAction,
  updateCurrentDatetime,
  updateOnlineStatus,
  zkkSelectedAction
} from './auth.actions';
import {selectRedirectTo, selectRoleAssignations, selectUser, selectUserProfileSettings} from './auth.selectors';
import {CdDialogService} from '~/app/cd-common/service/dialog.service';

export const ONLINE_CHECK_INTERVAL_MS = 30_000;

const RELEASE_NOTE_SHOWN_LOCAL_STORAGE_KEY = 'cd_lastShownReleaseNoteId';
const STOERUNGSMELDUNG_SHOWN_LOCAL_STORAGE_KEY = 'cd_lastShownStoerungsmeldungIds';
const STOERUNGSMELDUNG_CHECK_INTERVAL_MS = 300_000; // 5 minutes
const TIPP_READ_LOCAL_STORAGE_KEY = 'cd_readTippIds';
const TIPP_CHECK_INTERVAL_MS = 300_000;
const TIPP_NOTIFICATION_ID_PREFIX = 'tipp-';

function getTippNotificationId(tipp: Tipp): string {
  return `${TIPP_NOTIFICATION_ID_PREFIX}${tipp.documentId}-${tipp.updatedAt}`;
}

function getUserSettingsId(user: User): string {
  return UserHelper.isCisoUser(user) ? UserHelper.getZkk(user) : user.email;
}

@Injectable()
export class AuthEffects implements OnInitEffects {
  loadUserProfileSettingsAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      map(a => a.payload),
      mergeMap(user =>
        this.userProfileApi.getUserSettings(getUserSettingsId(user)).pipe(
          map(profileSettings => (profileSettings ? profileSettings : {})), // handle 204 gracefully (mock only)
          map(profileSettings => loadUserProfileSettingsSuccess({payload: profileSettings})),
          catchError(err => of(loadUserProfileSettingsFailure({error: serializeError(err)})))
        )
      )
    )
  );

  loadCargopushStatusInfoAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      mergeMap(() => this.checkCargoPushStatus())
    )
  );

  private checkCargoPushStatus(): Observable<ReturnType<typeof notificationCreate> | ReturnType<typeof loadCargoPushStatusInfoSuccess>> {
    return this.cargopushApiInternal.getCargoPushStatus().pipe(
      map(statusInfo => {
        if (!statusInfo.aktiv) {
          return notificationCreate({
            notification: NotificationHelper.createCDNotification({
              type: 'warning',
              showInNotificationCenter: true,
              title: $localize`:@@CARGO_PUSH_DISABLED_TITEL:Cargo Push vorübergehend deaktiviert`,
              message: statusInfo.bemerkung ?? $localize`:@@CARGO_PUSH_DISABLED_TEXT:Aufgrund von betrieblichen Problemen wurde Cargo Push deaktiviert.`
            })
          });
        } else {
          return loadCargoPushStatusInfoSuccess();
        }
      }),
      catchError(err => {
        const i18nToken = $localize`:@@ERROR_CARGO_PUSH_STATUS_DATA_LOAD:Cargo Push Status Informationen konnten nicht geladen werden.`;
        const errorAction = this.errorService.getConnectionOrUnknownNotificationErrorAction(err, i18nToken);
        return of(errorAction);
      })
    );
  }

  checkCargopushStatusInfoFromWebsocket$ = createEffect(() =>
    this.websocketService.watch('/user/notify/cargopush/status').pipe(
      map(cargopushStatusInfo => JSON.parse(cargopushStatusInfo) as CargopushStatusInfoApiModelInternal),
      map(statusInfo => {
        if (statusInfo.aktiv) {
          return notificationCreate({
            notification: NotificationHelper.createCDNotification({
              type: 'warning',
              showInNotificationCenter: true,
              title: $localize`:@@CARGO_PUSH_ENABLED_TITEL:Cargo Push wieder aktiviert`,
              message: statusInfo.bemerkung ?? $localize`:@@CARGO_PUSH_ENABLED_TEXT:Cargo Push wurde wieder aktiviert.`
            })
          });
        } else {
          return notificationCreate({
            notification: NotificationHelper.createCDNotification({
              type: 'warning',
              showInNotificationCenter: true,
              title: $localize`:@@CARGO_PUSH_DISABLED_TITEL:Cargo Push vorübergehend deaktiviert`,
              message: statusInfo.bemerkung ?? $localize`:@@CARGO_PUSH_DISABLED_TEXT:Aufgrund von betrieblichen Problemen wurde Cargo Push deaktiviert.`
            })
          });
        }
      })
    )
  );

  checkTermsAndConditions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserProfileSettingsSuccess),
        mergeMap(action => {
          if (!action?.payload.allgemeineGeschaeftsbedingungen?.aktuelleAllgemeineGeschaeftsbedingungenAkzeptiert) {
            this.dialog.open(TermsAndConditionsComponent, {
              data: {
                isFirstTimeAcceptance: !action.payload.allgemeineGeschaeftsbedingungen?.timeStampZuletztAktzeptiert
              },
              dismissable: false
            });
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  checkSessionRecording$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserProfileSettingsSuccess),
        map(action => action.payload.sessionRecording),
        filter(() => this.onetrustService.hasUserAcceptedGroup(CookieGroup.LEISTUNGS_COOKIES)),
        filter(recording => !recording?.sessionRecordingAktiv),
        filter(recording => {
          // If never accepted, show dialog
          if (!recording?.datumZuletztGeaendert) return true;

          const expirationDate = DateService.addMonthsToDate(new Date(Date.parse(recording.datumZuletztGeaendert)), 3);

          // When expired return true to show dialog
          if (expirationDate <= this.simulatedDateService.getSimulatedDate()) {
            return true;
          }

          return false;
        }),
        tap(() => {
          this.dialog.open(SessionAnalyseComponent);
        }),
        mergeMap(() => EMPTY)
      ),
    {dispatch: false}
  );

  activateSessionRecording$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserProfileSettingsSuccess),
        filter(action => !action || !action.payload.sessionRecording || !!action.payload.sessionRecording.sessionRecordingAktiv),
        concatLatestFrom(() => this.store.pipe(select(selectUser))),
        mergeMap(([, user]) => {
          const sessionRecording = environment.sessionRecording;
          if (sessionRecording && user) {
            try {
              Smartlook.init(sessionRecording);
              Smartlook.record({forms: true, numbers: true, api: true});
              Smartlook.identify(user.email, {
                userType: user.type,
                isSupportUser: UserHelper.isSupportUser(user),
                email: user.email,
                name: user.name
              });
            } catch (error) {
              MonitoringHelper.noticeError(error, {context: 'activateSessionRecording'});
            }
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  deactivateSessionRecording$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changeUserProfileSettings),
        filter(action => !action.payload.sessionRecording || !action.payload.sessionRecording.sessionRecordingAktiv),
        mergeMap(() => {
          if (environment.sessionRecording) {
            try {
              Smartlook.disable(); // after this 'restart' is not possible
            } catch (error) {
              MonitoringHelper.noticeError(error, {context: 'deactivateSessionRecording'});
            }
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  changeUserProfileSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeUserProfileSettings),
      concatLatestFrom(() => [this.store.pipe(select(selectUser)), this.store.pipe(select(selectUserProfileSettings))]),
      mergeMap(([action, user, settings]) => {
        const newProfileSettings: BenutzerProfilEinstellungenApiModel = {...settings, ...action.payload};
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.userProfileApi.replaceUserSettings(getUserSettingsId(user!), newProfileSettings).pipe(
          map(profileSettings => changeUserProfileSettingsSuccess({payload: profileSettings})),
          catchError(err => of(changeUserProfileSettingsFailure({error: serializeError(err)})))
        );
      })
    )
  );

  persistZkkSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(zkkSelectedAction),
        delay(100), // delay to make sure the following newContextAction is processed first
        concatLatestFrom(() => this.store.pipe(select(selectRoleAssignations))),
        mergeMap(([action, roleAssignations]) => {
          this.localStorageService.setItem('zkkSelection2', JSON.stringify(action.newPartner));
          this.localStorageService.setItem('personalUserZkkAssignations', JSON.stringify(roleAssignations ?? {}));
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  setCookie$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(zkkSelectedAction),
        combineLatestWith(this.authService.getAccessToken()),
        delay(100),
        mergeMap(([action, accessToken]) => {
          if (accessToken) {
            const parsedToken = decodeJwt<ParsedJwtToken>(accessToken);
            const expires = new Date(parsedToken.exp * 1000).toUTCString();
            document.cookie = `cd_auth=${accessToken.replace('Bearer ', '')}; samesite=strict; expires=${expires}; path=/api/auftraege/`;
            document.cookie = `cd_x_zkk=${action.newPartner.partnerId}; samesite=strict; expires=${expires}; path=/api/auftraege/`;
          } else {
            document.cookie = 'cd_auth=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/api/auftraege/';
            document.cookie = `cd_x_zkk=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/api/auftraege/`;
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  deleteCookie$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(newContextAction),
        mergeMap(() => {
          document.cookie = 'cd_auth=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/api/auftraege/';
          document.cookie = `cd_x_zkk=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/api/auftraege/`;
          document.cookie = `cd_auth_seclink=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/internal/fakturen`;
          document.cookie = `cd_auth_seclink=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  clearZkkSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(newContextAction),
        mergeMap(() => {
          this.localStorageService.removeItem('zkkSelection2');
          this.localStorageService.removeItem('personalUserZkkAssignations');
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  afterCisoLoginLoadPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      map(a => a.payload),
      mergeMap(user => {
        if (UserHelper.isCisoUser(user) && !user.activePartner) {
          return this.partnerService.getPartnerById(user.id).pipe(
            map(partner => zkkSelectedAction({newPartner: partner, isSwitch: false})),
            catchError((error: HttpErrorResponse) => {
              const simplePartner: PartnerStammdatenApiModel = {partnerId: user.id, name: ''};
              return of(
                zkkSelectedAction({newPartner: simplePartner, isSwitch: false}),
                this.errorService.getConnectionOrUnknownNotificationErrorAction(error, $localize`:@@ZKK_SELECTION_SERVER_ERROR:Bei der Abfrage der Partner ist ein Fehler aufgetreten.`)
              );
            })
          );
        } else {
          return EMPTY;
        }
      })
    )
  );

  afterLoginForPersonalUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      map(a => a.payload),
      filter(user => UserHelper.isPersonalUser(user)),
      mergeMap(() => {
        const activePartner = this.localStorageService.getItem('zkkSelection2');
        const personalUserZkkAssignations = this.localStorageService.getItem('personalUserZkkAssignations');
        if (activePartner && personalUserZkkAssignations) {
          return [queryRoleAssignationsSuccess({response: JSON.parse(personalUserZkkAssignations), doZkkSelect: false}), zkkSelectedAction({newPartner: JSON.parse(activePartner), isSwitch: false})];
        } else {
          return [queryRoleAssignations()];
        }
      })
    )
  );

  doQueryRoleAssignations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queryRoleAssignations),
      mergeMap(() =>
        this.autorisierungenApiInternal.getMeineRollenZuweisung().pipe(
          map(rollenZuweisung => queryRoleAssignationsSuccess({response: rollenZuweisung, doZkkSelect: true})),
          catchError(err =>
            of(this.errorService.getConnectionOrUnknownNotificationErrorAction(err, $localize`:@@AUTORISIERUNGEN_SERVER_ERROR:Bei der Abfrage der verfügbaren Rollen ist ein Fehler aufgetreten.`))
          )
        )
      )
    )
  );

  selectZkkOrRedirectForRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queryRoleAssignationsSuccess),
      filter(a => a.doZkkSelect),
      mergeMap(({response}) => {
        if (response.rollenzuweisungenKunde?.length === 1) {
          return this.partnerService.getPartnerById(response.rollenzuweisungenKunde[0].zkk, true).pipe(map(r => zkkSelectedAction({newPartner: r, isSwitch: false})));
        } else if ((response.rollenzuweisungenKunde?.length ?? 0) > 1) {
          return from(this.router.navigateByUrl('/auth/personalUserZkkSelection')).pipe(mergeMap(() => EMPTY));
        } else if (response.rollenzuweisungenIntern?.length) {
          // support user or storage cockpit user can choose all zkk:
          return from(this.router.navigateByUrl('/auth/zkkSelection')).pipe(mergeMap(() => EMPTY));
        } else {
          return from(this.router.navigateByUrl('/auth/personalUserZkkSelection')).pipe(
            map(() =>
              notificationCreate({
                notification: NotificationHelper.createCDNotification({
                  type: 'error',
                  showInNotificationCenter: true,
                  title: $localize`:@@AUTORISIERUNGEN_NO_ZKK_AVAILABLE:Die Abfrage der verfügbaren Rollen hat für den persönlichen Benutzer keine Ergebnisse geliefert.`
                })
              })
            )
          );
        }
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutAction),
        mergeMap(() => {
          this.authService.logout();
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  /**
   * There are multiple redirect scenarios:
   *
   * | No. | User    | Condition                                                                      | Redirect Target                          |
   * |-----|---------|--------------------------------------------------------------------------------|------------------------------------------|
   * | 1   | Ciso | After login with a specific redirect URL in the oauth state                    | Redirect to this URL                     |
   * | 2   | Ciso | After login without a specific redirect URL                                    | Redirect to the last visited URL (desiredRedirect) |
   * | 3a   | Support | After login and no zkk selection present                                       | Redirect to zkk selection (all zkk)                |
   * | 3b   | Personal | After login and no zkk selection present                                      | Redirect to zkk selection (available zkk)          |
   * | 4   | Support + Personal | After login and zkk selection present with a specific redirect URL in the oauth state | Redirect to this URL                     |
   * | 5   | Support + Personal | After login and zkk selection present without a specific redirect URL          | Redirect to the last visited URL (desiredRedirect) |
   * | 6   | Support + Personal | After zkk selection directly after login with a specific redirect URL in the oauth state | Redirect to this URL                     |
   * | 7   | Support + Personal | After zkk selection directly after login without a specific redirect URL       | Redirect to the last visited URL (desiredRedirect) |
   * | 8   | Support + Personal | After zkk selection switch                                          | Redirect to home |
   * | 14  | all     | No redirect on page reload (auth from local storage)                           | -                                        |
   *
   * Assumption: The login redirects to the top level of the application "/".
   */
  redirectAfterLoginOrZkkSelection$ = createEffect(
    () =>
      merge(
        // 1+2
        this.actions$.pipe(
          ofType(loggedInAction),
          filter(() => '/' === this.getCurrentUrl()), // only after login
          filter(a => UserHelper.isCisoUser(a.payload)),
          concatLatestFrom(() => [this.store.pipe(select(selectRedirectTo))]),
          mergeMap(([, redirectTo]) => this.router.navigateByUrl(this.authService.getRedirectUrl() ?? redirectTo ?? '/home'))
        ),
        // 4+5+6+7
        this.actions$.pipe(
          ofType(zkkSelectedAction),
          filter(a => !a.isSwitch),
          filter(() => ['/', '/auth/zkkSelection', '/auth/personalUserZkkSelection'].includes(this.getCurrentUrl())), // only after login or manual zkk selection
          concatLatestFrom(() => this.store.pipe(select(selectRedirectTo))),
          mergeMap(([, redirectTo]) => this.router.navigateByUrl(this.authService.getRedirectUrl() ?? redirectTo ?? '/home'))
        ),
        // 8
        this.actions$.pipe(
          ofType(zkkSelectedAction),
          filter(a => a.isSwitch),
          mergeMap(() => this.router.navigateByUrl('/home'))
        )
      ),
    {dispatch: false}
  );

  createDesiredRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerRequestAction),
      map(action => action.payload.event.url),
      filter(
        url =>
          !!url &&
          !/^\/(?:\?.*)?$/.test(url) && // ignore home page (with optional query params from oauth state)
          !url.startsWith('/auth/zkkSelection') &&
          !url.startsWith('/auth/personalUserZkkSelection') &&
          !url.startsWith('/auth/login') &&
          !url.startsWith('/direct') &&
          !url.startsWith('/ebooking/transports/print') &&
          !url.startsWith('/settings/password')
      ),
      debounceTime(1000), // optimize performance
      map(url => {
        if (url.startsWith('/ebooking/wagon-scheduling/')) {
          return setDesiredRedirectAction({url: url.replace(/\d{2}-\d+.*$/, '')});
        }
        return setDesiredRedirectAction({url: url});
      })
    )
  );

  storeRedirectInLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setDesiredRedirectAction),
        mergeMap(action => {
          this.forwardToLastAreaService.persistForwardToLocalStorage({
            date: new Date(),
            url: action.url
          });
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  onInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authInitAction),
      map(() => setDesiredRedirectAction({url: this.forwardToLastAreaService.getCurrentForwardSettingsOrDefault('/home')}))
    )
  );

  /**
   * This checks if the user is already logged in when the app is loaded and triggers a `loggedInAction` if neccessary.
   *
   * There are several things to consider:
   * 1. The user should be loaded _before_ the AuthGuard is called
   * 2. The `loggedInAction` should be dispatched _after_ AuthEffects are initialized (otherwise the zkkSelection is not performed)
   * 3. The "initialNavigation" of the angular router must already be performed or in progress when the `loggedInAction` (or zkkSelection) is dispatched
   * (otherwise we cannot decide if we are on a specific path already or need to redirect)
   */
  onInitCheckLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authInitAction),
        mergeMap(() => {
          this.authService.checkLoggedIn();
          this.router.initialNavigation();
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  showReleaseNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      mergeMap(() => this.cmsService.getCurrentReleaseNote()),
      filter(notEmpty),
      // known limitation: when switching the language, the release note will be shown again (as different languages have different ids)
      filter(releaseNote => this.localStorageService.getItem(RELEASE_NOTE_SHOWN_LOCAL_STORAGE_KEY) !== `${releaseNote.documentId}`),
      map(releaseNote => {
        this.localStorageService.setItem(RELEASE_NOTE_SHOWN_LOCAL_STORAGE_KEY, `${releaseNote.documentId}`); // side effect!
        return notificationCreate({
          notification: NotificationHelper.createCDNotification({
            type: 'info',
            showInNotificationCenter: true,
            title: releaseNote.Titel,
            message: releaseNote.Kurztext,
            directActions: [{label: $localize`:@@RELEASE_NOTE_DETAILS_LINK:Details`, actions: {routerLink: `/release-notes/${releaseNote.documentId}`}}]
          })
        });
      })
    )
  );

  showStoerungsmeldungNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      switchMap(
        () => timer(0, STOERUNGSMELDUNG_CHECK_INTERVAL_MS).pipe(takeUntil(this.actions$.pipe(ofType(logoutAction)))) // immediate check after login
      ),
      mergeMap(() => this.cmsService.getActiveStoerungsmeldungen()),
      filter(notEmpty),
      map(stoerungsmeldungen => {
        const shownStoerungsmeldungen = this.getShownStoerungsmeldungen();
        return stoerungsmeldungen.filter(meldung => {
          const isAlreadyShown = shownStoerungsmeldungen.some(shown => shown.id === meldung.documentId && shown.updatedAt === meldung.updatedAt);
          return !isAlreadyShown;
        });
      }),
      mergeMap(newStoerungsmeldungen => {
        // Mark all new stoerungsmeldungen as shown
        const shownStoerungsmeldungen = this.getShownStoerungsmeldungen();
        const updatedShown = this.getUpdatedShown(shownStoerungsmeldungen, newStoerungsmeldungen, this.simulatedDateService.getSimulatedDate());
        this.localStorageService.setItem(STOERUNGSMELDUNG_SHOWN_LOCAL_STORAGE_KEY, JSON.stringify(updatedShown));

        // Create notifications for each new stoerungsmeldung
        return newStoerungsmeldungen.map(stoerungsmeldung =>
          notificationCreate({
            notification: NotificationHelper.createCDNotification({
              type: 'info',
              showInNotificationCenter: true,
              title: stoerungsmeldung.Titel,
              message: stoerungsmeldung.Kurztext,
              directActions: [{label: $localize`:@@STOERUNGSMELDUNG_DETAILS_LINK:Details`, actions: {routerLink: `/disruption-alerts/${stoerungsmeldung.documentId}`}}]
            })
          })
        );
      })
    )
  );

  syncTippNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInAction),
      switchMap(() => timer(0, TIPP_CHECK_INTERVAL_MS).pipe(takeUntil(this.actions$.pipe(ofType(logoutAction))))),
      switchMap(() => this.cmsService.getActiveTipps()),
      concatLatestFrom(() => this.store.pipe(select(selectPanelNotifications))),
      mergeMap(([activeTipps, panelNotifications]) => {
        this.activeTipps = activeTipps;
        const read = this.getShownCmsDocuments(TIPP_READ_LOCAL_STORAGE_KEY);
        const unreadTipps = activeTipps.filter(tipp => !read.some(r => r.id === tipp.documentId && r.updatedAt === tipp.updatedAt));
        const unreadIds = new Set(unreadTipps.map(tipp => getTippNotificationId(tipp)));
        const shownIds = new Set(panelNotifications.map(n => n.id));

        return [
          ...panelNotifications.filter(n => n.id.startsWith(TIPP_NOTIFICATION_ID_PREFIX) && !unreadIds.has(n.id)).map(notification => notificationDelete({notification, doBroadcast: false})),
          ...unreadTipps
            .filter(tipp => !shownIds.has(getTippNotificationId(tipp)))
            .map(tipp =>
              notificationCreate({
                notification: NotificationHelper.createCDNotification({
                  id: getTippNotificationId(tipp),
                  type: 'info',
                  showInNotificationCenter: true,
                  title: tipp.Titel,
                  message: tipp.Kurztext,
                  directActions: [{label: $localize`:@@TIPP_DETAILS_LINK:Details`, actions: {routerLink: `/tips/${tipp.documentId}`}}]
                })
              })
            )
        ];
      })
    )
  );

  markTippNotificationsAsRead$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(notificationDelete),
          filter(a => !a.onlyRemoveInBell && a.notification.id.startsWith(TIPP_NOTIFICATION_ID_PREFIX)),
          map(a => this.activeTipps.filter(tipp => getTippNotificationId(tipp) === a.notification.id))
        ),
        this.actions$.pipe(
          ofType(notificationDeleteAll),
          map(() => this.activeTipps)
        )
      ).pipe(
        tap(tipps => {
          const read = this.getShownCmsDocuments(TIPP_READ_LOCAL_STORAGE_KEY).filter(r => this.activeTipps.some(tipp => tipp.documentId === r.id));
          const updatedRead = [...read.filter(r => !tipps.some(tipp => tipp.documentId === r.id)), ...tipps.map(tipp => ({id: tipp.documentId, updatedAt: tipp.updatedAt}))];
          this.localStorageService.setItem(TIPP_READ_LOCAL_STORAGE_KEY, JSON.stringify(updatedRead));
        })
      ),
    {dispatch: false}
  );

  newContext$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(zkkSelectedAction),
        filter(a => a.isSwitch)
      ),
      this.actions$.pipe(ofType(logoutAction))
    ).pipe(map(() => newContextAction()))
  );

  clearLocalStorageFilterSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(newContextAction),
        mergeMap(() => {
          Object.values(filterSettingsKeys).forEach(key => this.localStorageService.removeItem(key));
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  // TODO: Remove this effect after completing the personal user migration, when CISO accounts are no longer in use.
  setCookieCdAuthForCISO$ = createEffect(
    () =>
      this.authService.getAccessToken().pipe(
        mergeMap(accessToken => {
          if (accessToken) {
            const parsedToken = decodeJwt<ParsedJwtToken>(accessToken);
            const expires = new Date(parsedToken.exp * 1000).toUTCString();
            document.cookie = `cd_auth=${accessToken.replace('Bearer ', '')}; samesite=strict; expires=${expires}; path=/api/auftraege/`;
          } else {
            document.cookie = 'cd_auth=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/api/auftraege/';
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  setCurrentUserMonitoringAttributes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loggedInAction, zkkSelectedAction),
        concatLatestFrom(() => this.store.pipe(select(selectUser))),
        mergeMap(([, user]) => {
          if (user) {
            MonitoringHelper.setCurrentUserAttributes(user);
          }
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  deleteCurrentUserMonitoringAttributes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(newContextAction),
        mergeMap(() => {
          MonitoringHelper.resetCurrentUserAttributes();
          return EMPTY;
        })
      ),
    {dispatch: false}
  );

  offlineDetection$ = createEffect(() =>
    merge(fromEvent(window, 'online').pipe(map(() => true)), fromEvent(window, 'offline').pipe(map(() => false))).pipe(
      startWith(navigator.onLine),
      distinctUntilChanged(),
      concatMap(online =>
        online === false
          ? of(false)
          : interval(ONLINE_CHECK_INTERVAL_MS).pipe(
              startWith(0),
              concatMap(() =>
                this.requestSuccessInterceptor.successfulRequests$.pipe(
                  take(1),
                  timeout({
                    each: ONLINE_CHECK_INTERVAL_MS,
                    with: () => {
                      if (document.visibilityState !== 'visible') {
                        return EMPTY; // do not change/check status if tab is not active
                      }
                      return this.httpClient.head(`${document.baseURI}assets/icons/green_circle.svg?ping=${Date.now()}`, {observe: 'response'}).pipe(
                        map(r => r.ok),
                        catchError(() => of(false))
                      );
                    }
                  })
                )
              )
            )
      ),
      distinctUntilChanged(),
      map(online => updateOnlineStatus({online}))
    )
  );

  updateCurrentDateTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerRequestAction),
      throttleTime(100),
      map(() =>
        updateCurrentDatetime({
          currentDatetime: DateService.dateTimeToUTCDateTime(this.simulatedDateService.getSimulatedDate())
        })
      )
    )
  );

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private forwardToLastAreaService: ForwardToLastAreaService,
    private errorService: ErrorService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private store: Store,
    private simulatedDateService: SimulatedDateService,
    private httpClient: HttpClient,
    private requestSuccessInterceptor: RequestSuccessInterceptor,
    private userProfileApi: BenutzerprofilEinstellungenApi,
    private partnerService: PartnerService,
    private cargopushApiInternal: CargopushApiInternal,
    private websocketService: WebsocketService,
    private dialog: CdDialogService,
    private cmsService: CmsService,
    private autorisierungenApiInternal: AutorisierungenApiInternal,
    private onetrustService: OnetrustService
  ) {}

  private activeTipps: Tipp[] = [];

  ngrxOnInitEffects(): Action {
    return authInitAction({
      isoDateTime: DateService.dateTimeToUTCDateTime(this.simulatedDateService.getSimulatedDate()),
      online: navigator.onLine
    });
  }

  private getShownStoerungsmeldungen(): Array<{id: string; updatedAt: string}> {
    return this.getShownCmsDocuments(STOERUNGSMELDUNG_SHOWN_LOCAL_STORAGE_KEY);
  }

  private getShownCmsDocuments(localStorageKey: string): Array<{id: string; updatedAt: string}> {
    const stored = this.localStorageService.getItem(localStorageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.warn(`Failed to parse stored ${localStorageKey} data:`, error);
        return [];
      }
    }
    return [];
  }

  private getUpdatedShown(shownStoerungsmeldungen: ReturnType<AuthEffects['getShownStoerungsmeldungen']>, newStoerungsmeldungen: Stoerungsmeldung[], now: Date) {
    // Create a map of existing shown stoerungsmeldungen for quick lookup and updating
    const shownMap = new Map(shownStoerungsmeldungen.map(item => [item.id, item]));

    // Update existing entries and add new ones
    newStoerungsmeldungen.forEach(m => {
      shownMap.set(m.documentId, {
        id: m.documentId,
        updatedAt: m.updatedAt
      });
    });

    // Filter out entries older than 3 days
    const cutoffDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
    return Array.from(shownMap.values()).filter(item => new Date(item.updatedAt) >= cutoffDate) satisfies ReturnType<AuthEffects['getShownStoerungsmeldungen']>;
  }

  /**
   * Returns current URL, or - if a navigation is currently in progress - the URL of the navigation. Any query parameters are removed.
   * This is necessary because the (implicit) login on a page reload is faster than the initial navigation, so with `Router.url` we'd falsly assume to be on "/".
   */
  private getCurrentUrl(): string {
    const fullUrl = this.router.getCurrentNavigation()?.initialUrl.toString() ?? this.router.url;
    return fullUrl.replace(/\?.*/, '');
  }
}
